/*
 * Copyright (C) 2024 Das Land Schleswig-Holstein vertreten durch den
 * Ministerpräsidenten des Landes Schleswig-Holstein
 * Staatskanzlei
 * Abteilung Digitalisierung und zentrales IT-Management der Landesregierung
 *
 * Lizenziert unter der EUPL, Version 1.2 oder - sobald
 * diese von der Europäischen Kommission genehmigt wurden -
 * Folgeversionen der EUPL ("Lizenz");
 * Sie dürfen dieses Werk ausschließlich gemäß
 * dieser Lizenz nutzen.
 * Eine Kopie der Lizenz finden Sie hier:
 *
 * https://joinup.ec.europa.eu/collection/eupl/eupl-text-eupl-12
 *
 * Sofern nicht durch anwendbare Rechtsvorschriften
 * gefordert oder in schriftlicher Form vereinbart, wird
 * die unter der Lizenz verbreitete Software "so wie sie
 * ist", OHNE JEGLICHE GEWÄHRLEISTUNG ODER BEDINGUNGEN -
 * ausdrücklich oder stillschweigend - verbreitet.
 * Die sprachspezifischen Genehmigungen und Beschränkungen
 * unter der Lizenz sind dem Lizenztext zu entnehmen.
 */
import { KeycloakDefaults } from '@administration/keycloak-shared';
import { createStateResource, StateResource } from '@common/tech-shared';
import { inject, Injectable } from '@angular/core';
import KcAdminClient from '@keycloak/keycloak-admin-client';
import ClientRepresentation from '@keycloak/keycloak-admin-client/lib/defs/clientRepresentation';
import GroupRepresentation from '@keycloak/keycloak-admin-client/lib/defs/groupRepresentation';
import MappingsRepresentation from '@keycloak/keycloak-admin-client/lib/defs/mappingsRepresentation';
import { RequiredActionAlias } from '@keycloak/keycloak-admin-client/lib/defs/requiredActionProviderRepresentation';
import { RoleMappingPayload } from '@keycloak/keycloak-admin-client/lib/defs/roleRepresentation';
import UserRepresentation from '@keycloak/keycloak-admin-client/lib/defs/userRepresentation';
import { isNil, omit } from 'lodash-es';
import { concatMap, forkJoin, from, map, mergeMap, Observable, throwError } from 'rxjs';
import { HIDDEN_CLIENT_ROLES } from './role.model';
import { AdminRoles, ClientMapping, ClientRoles, RoleMappings, User } from './user.model';

import * as _ from 'lodash-es';

@Injectable({
  providedIn: 'root',
})
export class UserRepository {
  private readonly kcAdminClient = inject(KcAdminClient);

  public static readonly ALFA_CLIENT_NAME: string = 'alfa';
  public static readonly ADMIN_CLIENT_NAME: string = 'admin';

  public createInKeycloak(user: User): Observable<User> {
    return from(this.kcAdminClient.users.create(omit(user, 'groupIds'))).pipe(
      concatMap(async (response: { id: string }): Promise<{ id: string }> => {
        await this._updateUserRoles(response.id, user.clientRoles);
        return response;
      }),
      map((response: { id: string }): User => ({ ...user, id: response.id })),
    );
  }

  public saveInKeycloak(user: User): Observable<User> {
    const userToSave: Omit<User, 'groupIds'> = omit(user, 'groupIds');
    return from(this.kcAdminClient.users.update({ id: user.id }, userToSave)).pipe(
      concatMap(async (): Promise<void> => {
        await this._updateUserRoles(user.id, user.clientRoles);
        await this._updateUserGroups(user.id, user.groupIds);
      }),
      map((): User => user),
    );
  }

  async _updateUserGroups(userId: string, newGroupIds: string[]): Promise<void> {
    const oldUserGroups: string[] = await this._getOldUserGroupIds(userId);
    await this._deleteUserGroups(userId, newGroupIds, oldUserGroups);
    await this._addUserGroups(userId, newGroupIds, oldUserGroups);
  }

  async _getOldUserGroupIds(userId: string): Promise<string[]> {
    const oldUserGroupsReps: RoleMappingPayload[] = <RoleMappingPayload[]>(
      await this.kcAdminClient.users.listGroups({ id: userId })
    );
    return _.map(oldUserGroupsReps, 'id');
  }

  async _deleteUserGroups(userId: string, newGroupIds: string[], oldGroupIds: string[]): Promise<void> {
    await Promise.all(
      oldGroupIds
        .filter((group) => !newGroupIds.includes(group))
        .map(async (groupId: string) => {
          await this.kcAdminClient.users.delFromGroup({ id: userId, groupId });
        }),
    );
  }

  async _addUserGroups(userId: string, newGroupIds: string[], oldGroupIds: string[]): Promise<void> {
    await Promise.all(
      newGroupIds
        .filter((group) => !oldGroupIds.includes(group))
        .map(async (groupId: string) => {
          await this.kcAdminClient.users.addToGroup({ id: userId, groupId: groupId });
        }),
    );
  }

  async _updateUserRoles(userId: string, clientRoles: ClientRoles): Promise<void> {
    await this._updateUserRolesForClient(userId, clientRoles.admin, await this._getClientId(UserRepository.ADMIN_CLIENT_NAME));
    await this._updateUserRolesForClient(userId, clientRoles.alfa, await this._getClientId(UserRepository.ALFA_CLIENT_NAME));
    await this._updateUserRolesForAdmin(userId, clientRoles);
  }

  async _updateUserRolesForClient(userId: string, clientRoles: string[], clientId: string): Promise<void> {
    const roleMappings: RoleMappings = await this._getClientRoleMappings(userId, clientId, clientRoles);
    await this._deleteUserRoles(userId, roleMappings, clientId);
    await this._addUserRoles(userId, roleMappings, clientId);
  }

  async _getClientRoleMappings(userId: string, clientId: string, clientRoles: string[]): Promise<RoleMappings> {
    const newClientRoleMappings: RoleMappingPayload[] = await this._mapUserRoles(clientId, clientRoles);
    const oldClientRoleMappings: RoleMappingPayload[] = await this._getOldUserRoleMappings(userId, clientId);
    return { newClientRoleMappings, oldClientRoleMappings };
  }

  async _getOldUserRoleMappings(userId: string, clientId: string): Promise<RoleMappingPayload[]> {
    return <RoleMappingPayload[]>await this.kcAdminClient.users.listClientRoleMappings({
      id: userId,
      clientUniqueId: clientId,
    });
  }

  async _deleteUserRoles(userId: string, roleMappings: RoleMappings, clientId: string): Promise<void> {
    const rolesToDelete: RoleMappingPayload[] = this.getRolesToDelete(roleMappings);
    if (rolesToDelete.length > 0) {
      await this._deleteUserRolesInKeycloak(userId, clientId, rolesToDelete);
    }
  }

  private getRolesToDelete(roleMappings: RoleMappings): RoleMappingPayload[] {
    return roleMappings.oldClientRoleMappings.filter(
      (role: RoleMappingPayload) => !_.map(roleMappings.newClientRoleMappings, 'name').includes(role.name),
    );
  }

  async _addUserRoles(userId: string, roleMappings: RoleMappings, clientId: string): Promise<void> {
    const rolesToAdd: RoleMappingPayload[] = this.getRolesToAdd(roleMappings);
    if (rolesToAdd.length > 0) {
      await this._addUserRolesInKeycloak(userId, clientId, rolesToAdd);
    }
  }

  private getRolesToAdd(roleMappings: RoleMappings): RoleMappingPayload[] {
    return roleMappings.newClientRoleMappings.filter(
      (role: RoleMappingPayload) => !_.map(roleMappings.oldClientRoleMappings, 'name').includes(role.name),
    );
  }

  async _getClientId(client: string): Promise<string | undefined> {
    const clients: ClientRepresentation[] = await this.kcAdminClient.clients.find({ clientId: client });
    return clients?.[0].id;
  }

  public getAvailableClientRoles(): Observable<ClientRoles> {
    return forkJoin({
      admin: from(this._getAvailableRolesForClient(UserRepository.ADMIN_CLIENT_NAME)),
      alfa: from(this._getAvailableRolesForClient(UserRepository.ALFA_CLIENT_NAME)),
    });
  }

  async _getAvailableRolesForClient(client: string): Promise<string[]> {
    const clientId: string = await this._getClientId(client);
    const roles: RoleMappingPayload[] = <RoleMappingPayload[]>await this.kcAdminClient.clients.listRoles({ id: clientId });
    return this._filterHiddenRoles(client, _.map(roles, 'name'));
  }

  _filterHiddenRoles(client: string, roleNames: string[]): string[] {
    const hiddenRoles: string[] = HIDDEN_CLIENT_ROLES[client] ?? [];
    return roleNames.filter((roleName: string) => !hiddenRoles.includes(roleName));
  }

  async _mapUserRoles(clientId: string, userRoles: string[]): Promise<RoleMappingPayload[]> {
    const roles: RoleMappingPayload[] = <RoleMappingPayload[]>await this.kcAdminClient.clients.listRoles({ id: clientId });
    return roles.filter((role: RoleMappingPayload): boolean => userRoles.includes(role.name));
  }

  async _addUserRolesInKeycloak(userId: string, clientId: string, roles: RoleMappingPayload[]): Promise<void> {
    await this.kcAdminClient.users.addClientRoleMappings({
      id: userId,
      clientUniqueId: clientId,
      roles,
    });
  }

  async _deleteUserRolesInKeycloak(userId: string, clientId: string, roles: RoleMappingPayload[]): Promise<void> {
    await this.kcAdminClient.users.delClientRoleMappings({
      id: userId,
      clientUniqueId: clientId,
      roles,
    });
  }

  async _updateUserRolesForAdmin(userId: string, clientRoles: ClientRoles): Promise<void> {
    if (clientRoles.admin.includes(AdminRoles.ADMIN)) {
      await this._updateUserRolesForClient(
        userId,
        [
          KeycloakDefaults.clients.realmManagement.roles.managerUsers.roleName,
          KeycloakDefaults.clients.realmManagement.roles.queryGroups.roleName,
          KeycloakDefaults.clients.realmManagement.roles.viewClients.roleName,
        ],
        await this._getClientId(KeycloakDefaults.clients.realmManagement.clientName),
      );
    }
  }

  _sendActivationMail(userId: string): void {
    this.kcAdminClient.users.executeActionsEmail({
      id: userId,
      actions: [RequiredActionAlias.VERIFY_EMAIL, RequiredActionAlias.UPDATE_PASSWORD],
      lifespan: 3600 * 24 * 7,
    });
  }

  _handleCreateError(err: unknown): Observable<never> {
    console.error('Error on create user in Keycloak:', err);
    return throwError(() => new Error('An error occurred while creating the user.'));
  }

  _handleSaveError(err: unknown): Observable<never> {
    console.error('Error on patch user in Keycloak:', err);
    return throwError(() => new Error('An error occurred while saving the user.'));
  }

  public deleteUser(userId: string): Observable<void> {
    return from(this.kcAdminClient.users.del({ id: userId }));
  }

  public getUsers(): Observable<User[]> {
    return from(this.kcAdminClient.users.find()).pipe(
      map((userReps: UserRepresentation[]): User[] =>
        userReps.map((userReps: UserRepresentation): User => this._mapToUser(userReps)),
      ),
      mergeMap(
        (users: User[]): Observable<User[]> =>
          forkJoin(users.map((user: User): Observable<User> => this.addInformationToUser(user))),
      ),
    );
  }

  _mapToUser(userRepresentation: UserRepresentation): User {
    return {
      id: userRepresentation.id,
      email: userRepresentation.email,
      username: userRepresentation.username,
      firstName: userRepresentation.firstName,
      lastName: userRepresentation.lastName,
      enabled: userRepresentation.enabled,
      groups: null,
      clientRoles: {
        alfa: [],
        admin: [],
      },
    };
  }

  public getUserById(userId: string): Observable<StateResource<User>> {
    return this.getUserByIdWithoutRolesAndGroups(userId).pipe(
      mergeMap((user: User): Observable<User> => this.addInformationToUser(user)),
      map((user: User): StateResource<User> => createStateResource(user)),
    );
  }

  private getUserByIdWithoutRolesAndGroups(userId: string): Observable<User> {
    return from(this.kcAdminClient.users.findOne({ id: userId })) as Observable<User>;
  }

  private addInformationToUser(user: User): Observable<User> {
    return forkJoin([this.getUserGroups(user), this.getClientRoles(user)]).pipe(
      map(
        ([groups, clientRoles]: [string[], ClientRoles]): User => ({
          ...user,
          groups,
          clientRoles,
        }),
      ),
    );
  }

  private getUserGroups(user: User): Observable<string[]> {
    return from(this.kcAdminClient.users.listGroups({ id: user.id })).pipe(
      map((groups: GroupRepresentation[]): string[] => _.map(groups, 'name')),
    );
  }

  private getClientRoles(user: User): Observable<ClientRoles> {
    return from(this.kcAdminClient.users.listRoleMappings({ id: user.id })).pipe(
      map((roleMappings: MappingsRepresentation): ClientRoles => {
        return {
          alfa: this.mapToClientRoleNames(roleMappings, UserRepository.ALFA_CLIENT_NAME),
          admin: this.mapToClientRoleNames(roleMappings, UserRepository.ADMIN_CLIENT_NAME),
        };
      }),
    );
  }

  private mapToClientRoleNames(roleMappings: MappingsRepresentation, client: string): string[] {
    const clientMappingsAlfa: ClientMapping | undefined = roleMappings?.clientMappings?.[client];

    if (isNil(clientMappingsAlfa)) {
      return [];
    }

    return _.map(clientMappingsAlfa.mappings, 'name');
  }
}
